/**
 * VÉRAULT Portal — Request a Trip Modal (Wave 26.13 — VIP)
 *
 * The quiet wish-form. A past or current client opens this from their portal
 * to send a future-trip dream straight into the Advisor's inbox.
 *
 * Calm editorial form: serif eyebrow, generous spacing, suggestive placeholders.
 * No required dates — only a destination idea and (optionally) a timeframe.
 */
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Loader2, CheckCircle2 } from "lucide-react";
import { Surface, Heading, PortalButton, api } from "../index";

const BUDGET_BANDS = [
  { id: "comfortable", label: "Comfortable" },
  { id: "elevated", label: "Elevated" },
  { id: "rarefied", label: "Rarefied" },
  { id: "no_ceiling", label: "No ceiling" },
];

const inputCls =
  "w-full px-3.5 py-2.5 rounded-md bg-white border border-[rgba(0,0,0,0.1)] " +
  "font-body text-[14px] text-[hsl(var(--foreground))] " +
  "placeholder:text-[hsl(var(--foreground-muted))] placeholder:italic placeholder:font-accent " +
  "focus:outline-none focus:border-[hsl(var(--accent-gold))] focus:ring-1 focus:ring-[hsl(var(--accent-gold))]/30 " +
  "transition-colors";

const labelCls =
  "block font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-1.5";

export function RequestTripModal({ open, onClose, onSubmitted }) {
  const [form, setForm] = React.useState({
    destination_idea: "",
    timeframe: "",
    occasion: "",
    travelers_hint: "",
    budget_band: "",
    notes: "",
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!open) {
      // Reset after the close animation
      const t = setTimeout(() => {
        setForm({
          destination_idea: "",
          timeframe: "",
          occasion: "",
          travelers_hint: "",
          budget_band: "",
          notes: "",
        });
        setDone(false);
        setError(null);
      }, 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Close on Escape
  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape" && !submitting) onClose?.();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, submitting, onClose]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (!form.destination_idea.trim()) {
      setError("Tell us where you're dreaming of.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/client-portal/trip-requests", form);
      setDone(true);
      onSubmitted?.();
      setTimeout(() => {
        onClose?.();
      }, 1800);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't send your request. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/40 backdrop-blur-sm"
          onClick={() => !submitting && onClose?.()}
          data-testid="request-trip-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.22, 0.61, 0.36, 1] }}
            className="w-full sm:max-w-xl max-h-[92vh] overflow-y-auto bg-[hsl(var(--background))] rounded-t-2xl sm:rounded-2xl shadow-floating border border-[rgba(0,0,0,0.06)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between gap-4 px-6 pt-5 pb-3 bg-[hsl(var(--background))] border-b border-[rgba(0,0,0,0.04)]">
              <div className="min-w-0">
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent-gold))]">
                  A future experience
                </p>
                <h2 className="mt-1 font-serif text-[22px] sm:text-[26px] leading-tight text-[hsl(var(--foreground))]">
                  Where shall we send you next?
                </h2>
              </div>
              <button
                type="button"
                onClick={() => !submitting && onClose?.()}
                className="shrink-0 size-9 rounded-full flex items-center justify-center text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background-paper))] transition-colors disabled:opacity-30"
                disabled={submitting}
                aria-label="Close"
                data-testid="request-trip-close"
              >
                <X className="size-4" strokeWidth={1.5} />
              </button>
            </div>

            {done ? (
              <div className="px-6 py-12 text-center" data-testid="request-trip-success">
                <motion.div
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 200, damping: 18 }}
                  className="inline-flex items-center justify-center size-14 rounded-full bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] mb-4"
                >
                  <CheckCircle2 className="size-7" strokeWidth={1.25} />
                </motion.div>
                <h3 className="font-serif text-[22px] text-[hsl(var(--foreground))]">
                  We've received your wish.
                </h3>
                <p className="mt-2 font-accent italic text-[14.5px] text-[hsl(var(--foreground-secondary))] max-w-sm mx-auto">
                  Your advisor will be in touch shortly to begin shaping it.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="px-6 py-5 space-y-5" data-testid="request-trip-form">
                <p className="font-accent italic text-[14.5px] text-[hsl(var(--foreground-secondary))] leading-relaxed">
                  Share the seed of an idea — a place, a feeling, a season. We'll
                  reach out and design it from there.
                </p>

                <div>
                  <label className={labelCls} htmlFor="rt-destination">
                    Destination or idea <span className="text-[hsl(var(--accent-terracotta))]">*</span>
                  </label>
                  <input
                    id="rt-destination"
                    type="text"
                    value={form.destination_idea}
                    onChange={update("destination_idea")}
                    placeholder="Marrakech in spring · a quiet villa in Puglia · 'somewhere with snow'"
                    className={inputCls}
                    autoFocus
                    data-testid="request-trip-destination"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls} htmlFor="rt-timeframe">Timeframe</label>
                    <input
                      id="rt-timeframe"
                      type="text"
                      value={form.timeframe}
                      onChange={update("timeframe")}
                      placeholder="October '26 · sometime next spring"
                      className={inputCls}
                      data-testid="request-trip-timeframe"
                    />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="rt-occasion">Occasion</label>
                    <input
                      id="rt-occasion"
                      type="text"
                      value={form.occasion}
                      onChange={update("occasion")}
                      placeholder="Anniversary · birthday · just because"
                      className={inputCls}
                      data-testid="request-trip-occasion"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls} htmlFor="rt-travelers">Who's coming</label>
                    <input
                      id="rt-travelers"
                      type="text"
                      value={form.travelers_hint}
                      onChange={update("travelers_hint")}
                      placeholder="The two of us · family of four"
                      className={inputCls}
                      data-testid="request-trip-travelers"
                    />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="rt-budget">Comfort level</label>
                    <select
                      id="rt-budget"
                      value={form.budget_band}
                      onChange={update("budget_band")}
                      className={inputCls + " appearance-none cursor-pointer"}
                      data-testid="request-trip-budget"
                    >
                      <option value="">Prefer not to say</option>
                      {BUDGET_BANDS.map((b) => (
                        <option key={b.id} value={b.id}>{b.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className={labelCls} htmlFor="rt-notes">Anything else on your mind</label>
                  <textarea
                    id="rt-notes"
                    rows={4}
                    value={form.notes}
                    onChange={update("notes")}
                    placeholder="The texture of what you're hoping for — pace, mood, places to avoid…"
                    className={inputCls + " resize-y min-h-[100px]"}
                    data-testid="request-trip-notes"
                  />
                </div>

                {error && (
                  <p
                    className="text-[13px] font-body text-[hsl(var(--accent-terracotta))]"
                    data-testid="request-trip-error"
                  >
                    {error}
                  </p>
                )}

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-[rgba(0,0,0,0.04)]">
                  <PortalButton
                    type="button"
                    variant="quiet"
                    size="sm"
                    onClick={() => onClose?.()}
                    disabled={submitting}
                    data-testid="request-trip-cancel"
                  >
                    Cancel
                  </PortalButton>
                  <PortalButton
                    type="submit"
                    variant="gold"
                    size="sm"
                    icon={
                      submitting ? (
                        <Loader2 className="size-3.5 animate-spin" strokeWidth={1.5} />
                      ) : (
                        <Sparkles className="size-3.5" strokeWidth={1.5} />
                      )
                    }
                    disabled={submitting}
                    data-testid="request-trip-submit"
                  >
                    {submitting ? "Sending…" : "Send the wish"}
                  </PortalButton>
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default RequestTripModal;
